/**
 * # DIMENSION subtype → 2nd subclass marker(s) + group-70 flag bits (ONE TRUTH)
 *
 * A DXF R2000+ DIMENSION entity is serialized as `100 AcDbEntity` → the common
 * `AcDbDimension` body → **a per-subtype SECOND subclass marker** that owns the
 * subtype-specific defpoints/params (13/14/15/16, 50, 40, …). Without that 2nd
 * marker the subtype fields are orphaned and strict readers (AutoCAD / ezdxf
 * `readfile`) treat the entity as malformed (finding `missing-subtype-subclass-
 * marker`, SL-3 P1).
 *
 * This module is the SINGLE source of truth for that subtype→marker mapping and
 * for the `AcDbDimension` group-70 flag bits, consumed by BOTH:
 *   - the dxf-io writer (`writer.ts` DIMENSION case) — as the by-construction
 *     safety-net that synthesizes the marker(s) for any marker-less dimension;
 *   - the drawing-model export boundary (`dxfBridge.entityConversion.ts`
 *     `toDxfEntity`) — which populates `subclassMarkers` from the typed
 *     `dimensionType` AND ORs in the block-exclusive bit-32 (see below).
 *
 * It lives in `@dxf-ai-editor/dxf-io` (not `drawing-model`) because `dxf-io` is
 * the LOWER package — `drawing-model` depends on it, so the writer can import it
 * directly while the conversion imports it from the package barrel, with NO
 * dependency cycle (PRIORITY #2 dep-graph red zone).
 *
 * ## Verified against (SL-3, source of truth):
 * - **Autodesk DXF R2018 reference** — "DIMENSION" + per-subtype "…Dimension
 *   Group Codes" pages. Group 70: "Values 0–6 are integer values that represent
 *   the dimension type. Values 32, 64, and 128 are bit values … (value 32 is
 *   always set in R13 and later releases)". 0=Rotated/horizontal/vertical
 *   (linear), 1=Aligned, 2=Angular, 3=Diameter, 4=Radius, 5=Angular-3-point,
 *   6=Ordinate; 32="the block reference (group code 2) is referenced by this
 *   dimension only".
 * - **ezdxf 1.4.3** `ezdxf/entities/dimension.py` `Dimension.export_entity`
 *   (the `dim_type == N` branch, lines ~606-633) — the authoritative marker
 *   emission, incl. the type-0 DUAL marker (`AcDbAlignedDimension` THEN the
 *   "empty but required" `AcDbRotatedDimension`). `Dimension.dimtype` masks
 *   `& 15`; the flag constants are `DIM_BLOCK_EXCLUSIVE = 32`,
 *   `DIM_ORDINATE_TYPE = 64`, `DIM_USER_LOCATION_OVERRIDE = 128`
 *   (`ezdxf/lldxf/const.py`). ezdxf's `graphicsfactory.py` sets
 *   `DIM_<subtype> | DIM_BLOCK_EXCLUSIVE` for EVERY dimension it creates.
 */

/**
 * Group-70 (dimtype) BINARY flag: the block reference (group code 2) is
 * referenced by this dimension only ("block-exclusive"). Autodesk: "always set
 * in R13 and later releases"; ezdxf `DIM_BLOCK_EXCLUSIVE = 32`. Every DIMENSION
 * we export owns an anonymous `*D###` geometry block (see
 * `assignAnonymousDimensionBlockNames`), so this bit is always set on export.
 */
export const DIM_BLOCK_EXCLUSIVE = 32;

/** Group-70 flag: ordinate type (bit 6). Used only with subtype 6; set = X-type. */
export const DIM_ORDINATE_TYPE = 64;

/** Group-70 flag: dimension text placed at a user-defined location (bit 7). */
export const DIM_USER_LOCATION_OVERRIDE = 128;

/** Low 3 bits of group 70 isolate the dimension subtype (0–6). */
export const DIM_SUBTYPE_MASK = 0x07;

/**
 * The 2nd subclass marker chain for each DIMENSION subtype (keyed by the low 3
 * bits of group 70). Everything after `AcDbDimension` — the subtype-specific
 * subclass(es) that own the per-subtype defpoints/angle/leader-length.
 *
 * Type 0 (Rotated / horizontal / vertical linear) is the DUAL-marker case:
 * ezdxf and AutoCAD BOTH emit `AcDbAlignedDimension` AND then the empty-but-
 * required `AcDbRotatedDimension`. Do not collapse it to a single marker.
 */
const SUBTYPE_MARKERS: Readonly<Record<number, readonly string[]>> = {
  0: ["AcDbAlignedDimension", "AcDbRotatedDimension"], // Rotated / linear (dual)
  1: ["AcDbAlignedDimension"], // Aligned
  2: ["AcDb2LineAngularDimension"], // Angular (2-line)
  3: ["AcDbDiametricDimension"], // Diameter
  4: ["AcDbRadialDimension"], // Radius
  5: ["AcDb3PointAngularDimension"], // Angular (3-point)
  6: ["AcDbOrdinateDimension"], // Ordinate
};

/**
 * The per-subtype 2nd subclass marker(s) for a DIMENSION whose group-70 value is
 * `dimensionType`. Masks off the binary flag bits (32/64/128) internally with
 * `& 7`, so the caller may pass the RAW group-70 value (which for R13+ dims
 * carries bit 32 and, for ordinates, bit 64) or a bare 0–6 subtype.
 *
 * Returns a NEW array each call (safe for the caller to spread / mutate).
 *
 * Undocumented subtype 8 (ARC_DIMENSION, DXF R2018) is a SEPARATE entity class
 * (`ArcDimension` / `AcDbArcDimension`) not modeled by our DIMENSION entity, so
 * it is intentionally out of scope; a `& 7` of 8 (→ 0) never occurs because the
 * typed model constrains `dimensionType` to 0–6. Any unmapped value returns an
 * empty list (the writer then emits `AcDbDimension` alone — no bogus marker).
 */
export function dimensionSubtypeSubclassMarkers(
  dimensionType: number,
): string[] {
  const subtype = dimensionType & DIM_SUBTYPE_MASK;
  return [...(SUBTYPE_MARKERS[subtype] ?? [])];
}
